import { useState, type FormEvent, type ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { getCurrencyExponent } from "@repo/api-schema";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { PersonPicker } from "./PersonPicker";
import { amountToMinor, minorStepFor } from "../lib/money";
import type { Group } from "../api/types";

export interface SettlementFormValues {
  payerId: string;
  payeeId: string;
  amount: number;
  note?: string;
}

/**
 * Records a direct payment from one participant to another. Deliberately much
 * simpler than the expense form: a payer, a payee, an amount (always in the
 * group currency), and an optional note — no items or splits.
 *
 * `defaultPayerId` / `defaultPayeeId` pre-fill the pickers (e.g. from a
 * "settle up" suggestion), so a member can settle a specific debt in one tap.
 */
export function SettlementForm({
  group,
  defaultPayerId,
  defaultPayeeId,
  defaultAmountMinor,
  submitLabel = "Record payment",
  pendingLabel = "Recording…",
  submitting,
  serverError,
  onSubmit,
  onCancel,
}: {
  group: Group;
  defaultPayerId?: string;
  defaultPayeeId?: string;
  defaultAmountMinor?: number;
  submitLabel?: string;
  pendingLabel?: string;
  submitting?: boolean;
  serverError?: string | null;
  onSubmit: (values: SettlementFormValues) => void | Promise<void>;
  onCancel?: () => void;
}): ReactNode {
  const people = group.participants;
  const [payerId, setPayerId] = useState<string | undefined>(defaultPayerId);
  const [payeeId, setPayeeId] = useState<string | undefined>(defaultPayeeId);
  const [amount, setAmount] = useState(() => {
    if (defaultAmountMinor === undefined) return "";
    const exp = getCurrencyExponent(group.currency);
    return (defaultAmountMinor / 10 ** exp).toFixed(exp);
  });
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);

  const minorAmount = amountToMinor(amount, group.currency);
  const amountValid = Number.isFinite(minorAmount) && minorAmount > 0;
  const distinct =
    payerId !== undefined && payeeId !== undefined && payerId !== payeeId;
  const canSubmit = amountValid && distinct && !submitting;

  // Picking a payer who is already the payee (or vice versa) swaps the two,
  // rather than being blocked — so a user can reverse the direction (e.g. a
  // pre-filled suggestion pointing the wrong way) without clearing the form.
  // In a two-person group this is the only way to change direction.
  function selectPayer(id: string): void {
    if (id === payeeId) setPayeeId(payerId);
    setPayerId(id);
  }
  function selectPayee(id: string): void {
    if (id === payerId) setPayerId(payeeId);
    setPayeeId(id);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setTouched(true);
    if (!payerId || !payeeId || !amountValid || payerId === payeeId) return;
    const values: SettlementFormValues = {
      payerId,
      payeeId,
      amount: minorAmount,
    };
    const trimmedNote = note.trim();
    if (trimmedNote !== "") values.note = trimmedNote;
    await onSubmit(values);
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <Label>Who paid?</Label>
        <PersonPicker
          label="Payer"
          value={payerId}
          people={people}
          onChange={selectPayer}
        />
      </div>

      <div className="flex items-center gap-2 text-on-surface-variant">
        <span className="h-px flex-1 bg-outline-variant/60" />
        <ArrowRight className="size-4" aria-hidden />
        <span className="h-px flex-1 bg-outline-variant/60" />
      </div>

      <div className="space-y-2">
        <Label>Who received it?</Label>
        <PersonPicker
          label="Payee"
          value={payeeId}
          people={people}
          onChange={selectPayee}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="settlement-amount">Amount ({group.currency})</Label>
        <Input
          id="settlement-amount"
          type="number"
          inputMode="decimal"
          min="0"
          step={minorStepFor(group.currency)}
          placeholder="0.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
        />
        {touched && amount !== "" && !amountValid ? (
          <p className="text-label-sm text-error">
            Enter an amount greater than zero.
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="settlement-note">Note (optional)</Label>
        <Input
          id="settlement-note"
          placeholder="Venmo, cash, bank transfer…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={120}
        />
      </div>

      {touched && payerId !== undefined && payerId === payeeId ? (
        <p className="text-label-sm text-error">
          Payer and payee must be different people.
        </p>
      ) : null}

      {serverError ? (
        <div className="rounded-sm bg-error-container px-4 py-3 text-label-md text-on-error-container">
          {serverError}
        </div>
      ) : null}

      <div className="flex justify-end gap-3 pt-2">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button type="submit" disabled={!canSubmit}>
          {submitting ? pendingLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
}
